import HeroSlider from "../components/HeroSlider";
import IndustriesBento from "../components/IndustriesBento";
import Stats from "../components/Stats";
import GlobalTradeNetwork from "../components/GlobalTradeNetwork";
import PartnersMarquee from "../components/PartnersMarquee";
import Certifications from "../components/Certifications";
import CTABanner from "../components/CTABanner";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PRODUCT_CATEGORIES } from "../lib/content";

export default function Home() {
  return (
    <div data-testid="home-page">
      <HeroSlider />

      {/* Intro block */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-10 h-px bg-[#F37021]" />
              <span className="text-eyebrow text-[#F37021]">The House</span>
            </div>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-[#0A1F3F]"
            >
              A trading
              <br />
              house, not a
              <br />
              broker.
            </motion.h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="text-lg sm:text-xl leading-relaxed text-[#0A1F3F]">
              BNM Group is an international trading and industrial supply company
              headquartered in Ankara, Turkey — operating across seven industries
              that move the global economy.
            </p>
            <p className="mt-6 text-base text-[#4B5668] leading-relaxed">
              From rail and aviation to petrochemicals, industrial machinery,
              agricultural commodities, heavy equipment and telecommunications
              infrastructure — we connect global suppliers and buyers through a
              network of trusted manufacturers, logistics providers and
              institutional partners.
            </p>
            <Link
              to="/about"
              data-testid="home-about-link"
              className="mt-10 inline-flex items-center gap-3 border border-[#0A1F3F] hover:bg-[#0A1F3F] hover:text-white px-8 py-5 text-xs uppercase tracking-[0.2em] font-semibold transition-colors"
            >
              Our story
              <ArrowUpRight className="w-4 h-4 arrow-icon" />
            </Link>
          </div>
        </div>
      </section>

      <Stats />
      <IndustriesBento />
      <GlobalTradeNetwork />

      {/* Product categories preview */}
      <section className="py-24 sm:py-32 bg-[#F4F5F8]" data-testid="products-preview">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end mb-14">
            <div className="md:col-span-7">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-10 h-px bg-[#F37021]" />
                <span className="text-eyebrow text-[#F37021]">What We Trade</span>
              </div>
              <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl leading-[1.02] text-[#0A1F3F]">
                Products across
                <br />
                four categories.
              </h2>
            </div>
            <div className="md:col-span-4 md:col-start-9">
              <Link
                to="/products"
                data-testid="products-view-all"
                className="inline-flex items-center gap-3 border border-[#0A1F3F] hover:bg-[#0A1F3F] hover:text-white px-8 py-5 text-xs uppercase tracking-[0.2em] font-semibold transition-colors"
              >
                All products
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PRODUCT_CATEGORIES.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-white border border-[#E3E6EC] hover-lift group"
                data-testid={`product-preview-${p.id}`}
              >
                <div className="aspect-[4/3] overflow-hidden bg-[#F4F5F8]">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-display font-black tracking-tighter text-xl text-[#0A1F3F]">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-xs text-[#4B5668] leading-relaxed">
                    {p.items.slice(0, 3).join(" · ")}
                    {p.items.length > 3 ? " +" : ""}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <PartnersMarquee />
      <Certifications />
      <CTABanner />
    </div>
  );
}
